/**
 * Hook React — gestion des notifications push Web (VAPID).
 *
 * Usage :
 *   const { isSupported, isSubscribed, isLoading, subscribe, unsubscribe } = usePushNotifications();
 */
import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// Convertit une clé publique VAPID (base64url) en Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64  = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw     = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

function subscriptionToPayload(sub) {
  const json = sub.toJSON();
  return {
    endpoint: json.endpoint,
    p256dh:   json.keys.p256dh,
    auth:     json.keys.auth,
  };
}

export default function usePushNotifications() {
  const [isSupported, setIsSupported]   = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading]       = useState(false);
  const [vapidKey, setVapidKey]         = useState(null);
  const [error, setError]               = useState(null);

  // Vérifier le support et charger la clé publique VAPID au montage
  useEffect(() => {
    const supported =
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window;

    setIsSupported(supported);
    if (!supported) return;

    // Charger la clé VAPID publique depuis le backend
    api.get('/push/vapid-public-key')
      .then((res) => {
        setVapidKey(res.data.public_key);
      })
      .catch(() => {
        // Serveur non configuré — push désactivé silencieusement
        setIsSupported(false);
      });

    // Vérifier si déjà abonné
    navigator.serviceWorker.ready.then((reg) => {
      reg.pushManager.getSubscription().then((sub) => {
        setIsSubscribed(!!sub);
      });
    });
  }, []);

  const subscribe = useCallback(async () => {
    if (!isSupported || !vapidKey) return;
    setIsLoading(true);
    setError(null);
    try {
      // Demander la permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setError('Permission refusée. Activez les notifications dans les réglages du navigateur.');
        return;
      }

      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly:      true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });

      await api.post('/push/subscribe', subscriptionToPayload(sub));
      setIsSubscribed(true);
    } catch (err) {
      console.error('[PUSH] Erreur abonnement :', err);
      setError('Impossible d\'activer les notifications. Réessayez.');
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, vapidKey]);

  const unsubscribe = useCallback(async () => {
    if (!isSupported) return;
    setIsLoading(true);
    setError(null);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.delete('/push/subscribe', { data: subscriptionToPayload(sub) });
        await sub.unsubscribe();
      }
      setIsSubscribed(false);
    } catch (err) {
      console.error('[PUSH] Erreur désabonnement :', err);
      setError('Impossible de désactiver les notifications.');
    } finally {
      setIsLoading(false);
    }
  }, [isSupported]);

  return { isSupported, isSubscribed, isLoading, error, subscribe, unsubscribe };
}
